import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin as sessionReplay } from '@amplitude/plugin-session-replay-browser'
import isChromiumEdge from '@/lib/is-chromium-edge'

/**
 * All analytics related code should be within this class which allows the
 * global usage of directives created (see OnClickAnalytics for an example) and methods
 * (used via this.$analytics.[method_name]) in other files.
 */

/**
 * This class contains the methods necessary for analytics tracking
 */

const sessionReplayPlugin = sessionReplay({ sampleRate: 1 })

export class Analytics {
  // private variable
  private static globalMetaData: { [key: string]: any } = {}

  /**
   * Sends a tracking events with some properties to the tracking services and returns the boolean association
   * of the response based on whether or not the calls were successful or not
   */
  static async track(eventName: string, analyticsMetaData: any = {}) {
    const isChromiumEdgeBrowser = isChromiumEdge()

    if (isChromiumEdgeBrowser) {
      analyticsMetaData.isChromiumBrowser = isChromiumEdgeBrowser
    }

    const amplitudeResponse = amplitude.track(eventName, { ...this.globalMetaData, ...analyticsMetaData })

    return !!amplitudeResponse
  }

  static startSessionReplayTracking() {
    amplitude.add(sessionReplayPlugin)
  }

  static stopSessionReplayTracking() {
    if (sessionReplayPlugin.name) amplitude.remove(sessionReplayPlugin.name)
  }

  // changes the users tracking preferences to be opted out so that their actions are not tracked
  static async optOut() {
    amplitude.setOptOut(true)
  }

  // identifies a user under a unique user ID
  static identify(userId: string | null, userProperties: Record<string, string | number>) {
    const identifyObj = new amplitude.Identify()

    amplitude.setUserId(userId || undefined)
    Object.entries(userProperties).forEach(([key, val]) => identifyObj.set(key, val))
    amplitude.identify(identifyObj)
  }

  // unidentifies the user
  static unidentify() {
    this.globalMetaData = {}
    amplitude.setUserId(undefined)
    amplitude.reset()
  }

  static setGlobalMetaData(metaData: { [key: string]: any }, options: { override: boolean } = { override: false }) {
    this.globalMetaData = options.override ? metaData : { ...this.globalMetaData, ...metaData }
  }

  static resetGlobalMetaData() {
    this.globalMetaData = {}
  }
}

const handleAnalytics = (event: any) => {
  const eventName = event.currentTarget.event_name
  Analytics.track(eventName)
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()

  const token = config.public.AMPLITUDE_API_KEY as string

  if (!token) throw new Error('AMPLITUDE_API_KEY missing')

  amplitude.add(sessionReplayPlugin)
  amplitude.init(token, undefined, { defaultTracking: { sessions: true }, minIdLength: 1 })

  nuxtApp.vueApp.directive('analytics', {
    mounted(el, binding) {
      el.event_name = binding.value
      el.addEventListener('click', handleAnalytics)
    },
    unmounted(el) {
      el.removeEventListener('click', handleAnalytics)
    },
  })

  return {
    provide: {
      analytics: Analytics,
    },
  }
})
